/**
 * Pages a user can pick as their "opening page" (Admin > Settings > Account).
 *
 * The key is what the API stores on the user (Users.OpeningPage); the path is where the
 * "/" (home) route and the post-sign-in redirect send them. Pages behind a main permission
 * category (the group toggles in Settings > Groups) are only offered to, and only opened for,
 * users who hold that category - mirroring NavigationLoader / RequireCategory - so the choice
 * can never land someone on a page they cannot see.
 *
 * Keep the keys in sync with OpeningPages in WebAPI/Controllers/SettingsController.cs.
 */
export const OPENING_PAGE_KEYS = ['dashboard', 'batches', 'capture', 'agents'] as const;

export type OpeningPageKey = (typeof OPENING_PAGE_KEYS)[number];

export type OpeningPageCategory = 'workflow' | 'capture' | 'agents';

export type OpeningPageOption = {
  key: OpeningPageKey;
  label: string;
  path: string;
  /** Main permission category needed to open the page; undefined = everyone. */
  category?: OpeningPageCategory;
};

/** The subset of the user-permissions response the checks below need. */
export type OpeningPagePermissions = {
  tenantFeatures?: { agentsEnabled?: boolean };
  user?: { isSuperAdmin?: boolean };
  subcategoryPermissions?: {
    hasWorkflowCategory?: boolean;
    hasCaptureCategory?: boolean;
    hasAgentsCategory?: boolean;
  };
};

export const OPENING_PAGES: OpeningPageOption[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { key: 'batches', label: 'Batches', path: '/workflows/batches', category: 'workflow' },
  { key: 'capture', label: 'Connect', path: '/connect', category: 'capture' },
  { key: 'agents', label: 'Agents', path: '/agents', category: 'agents' }
];

export const DEFAULT_OPENING_PAGE: OpeningPageKey = 'dashboard';

const CATEGORY_FLAG: Record<OpeningPageCategory, keyof NonNullable<OpeningPagePermissions['subcategoryPermissions']>> = {
  workflow: 'hasWorkflowCategory',
  capture: 'hasCaptureCategory',
  agents: 'hasAgentsCategory'
};

function isOpeningPageKey(value: unknown): value is OpeningPageKey {
  return typeof value === 'string' && (OPENING_PAGE_KEYS as readonly string[]).includes(value);
}

/**
 * Whether the user may open a page. Tenant switches apply before group permissions
 * and super-admin overrides. Before permissions load, the route guard has the final say.
 */
export function canOpenPage(page: OpeningPageOption, permissions?: OpeningPagePermissions): boolean {
  if (!page.category || !permissions) {
    return true;
  }

  if (page.category === 'agents' && permissions.tenantFeatures?.agentsEnabled !== true) {
    return false;
  }

  if (permissions.user?.isSuperAdmin === true) {
    return true;
  }

  return permissions.subcategoryPermissions?.[CATEGORY_FLAG[page.category]] === true;
}

/** The pages the user can choose from, in display order. */
export function getAccessibleOpeningPages(permissions?: OpeningPagePermissions): OpeningPageOption[] {
  return OPENING_PAGES.filter((page) => canOpenPage(page, permissions));
}

/**
 * Normalizes a stored value to a known, reachable key. Unknown values, and pages the
 * user is not allowed to open, fall back to the dashboard.
 */
export function getOpeningPageKey(value?: string | null, permissions?: OpeningPagePermissions): OpeningPageKey {
  const storedKey = value?.trim().toLowerCase();
  const key = storedKey === 'dispatch' ? 'capture' : storedKey;

  if (!isOpeningPageKey(key)) {
    return DEFAULT_OPENING_PAGE;
  }

  const page = OPENING_PAGES.find((option) => option.key === key);

  return page && canOpenPage(page, permissions) ? key : DEFAULT_OPENING_PAGE;
}

/** Route for a stored opening-page value (the dashboard when unset, unknown, or not reachable). */
export function getOpeningPagePath(value?: string | null, permissions?: OpeningPagePermissions): string {
  const key = getOpeningPageKey(value, permissions);

  return OPENING_PAGES.find((page) => page.key === key)?.path ?? '/dashboard';
}
